//shared containers
import { Footer } from '@/containers-m/Footer';
import { Navigation } from '/containers-m/';

//containers
import {
  Hero,
  WatchItRunYourFreight,
  HowItWorks,
  RunsOnYourStack,
  TheRestOfTheLineup,
  EndCTA,
} from '../containers';

//styles
import {
  PageWrapper,
  TrackingAndStatusUpdatesGlobalStyle,
} from './TrackingAndStatusUpdates.styles';

const hero = {
  slug: 'tracking-and-status-updates',
  title: 'Tracking & Status Updates',
  description:
    'Pulls carrier status on a schedule, chases the carriers that go quiet, escalates the ones that stay quiet, and pushes clean updates to your team and your customers. No check calls.',
  metric: '5–10 min',
  metricLabel: 'saved per tracked load',
};

const watchItRun = {
  title: 'A load’s gone quiet',
  stageHeadTitle:
    'A customer’s asking where load #LS-51204 is — and it’s gone quiet.',
  stageHeadMeta: 'Tracking · #LS-51204',
  rows: [
    {
      time: '09:14:10',
      text: 'No tracking ping on #LS-51204 for 4 hours.',
    },
    {
      time: '09:14:12',
      text: 'Reached the carrier — truck’s rolling, 40 min behind.',
    },
    {
      time: '09:14:40',
      text: 'ETA recalculated and written back to your TMS.',
    },
    {
      time: '09:14:42',
      text: 'Still inside the delivery window — nothing needed from you.',
    },
    {
      time: '09:14:45',
      text: 'Clean status pushed to the customer before they asked again.',
    },
  ],
  summaryText:
    'A load stopped reporting and the customer was getting anxious. Loadsmart AI chased the carrier, confirmed the truck was moving, updated the ETA, and sent the customer a clean status — the check calls your team never had to make.',
  agentBadge: 'Tracking Agent',
  escalationTitle: 'Freight Experts take the silence.',
  escalationText:
    'A carrier that genuinely will not respond is the exception, not the default — and it is the one case where a person is worth more than another automated request. Everything before that point runs without anyone picking up a phone.',
};

const howItWorks = {
  stepsTitleWhite: 'Goes and gets the update.',
  stepsTitleGradient: 'Then keeps going.',
  steps: [
    {
      title: 'Checks the window',
      description:
        'Confirms tracking has actually started on each pickup inside a set window, rather than waiting for someone to notice it hasn’t.',
    },
    {
      title: 'Requests what’s missing',
      description:
        'Asks the carrier directly the moment tracking data doesn’t show up — the same call a person would make, without the person.',
    },
    {
      title: 'Escalates on repeat misses',
      description:
        'A carrier that stays quiet becomes a flagged exception instead of a load that quietly goes dark.',
    },
    {
      title: 'Clears the upstream blocker',
      description:
        'Chases missing driver, truck and contact detail from the carrier so tracking can start in the first place.',
    },
  ],
  outcomes: [
    {
      title: 'Exceptions surface, not discovered',
      description:
        'A missed check-in at hour four gets escalated at hour four, not when a customer calls three days later.',
    },
    {
      title: 'On-time performance and exception rate',
      description:
        'Both move once tracking data flows automatically instead of on request.',
    },
    {
      title: 'Update latency the customer feels',
      description:
        'Visibility that improves internally but never reaches the person waiting on the load hasn’t solved anything.',
    },
  ],
};

const lineupCards = [
  {
    slug: 'document-collection-and-filing',
    title: 'Document Collection & Filing',
    description:
      'We capture every quote, tender, rate con, BOL, POD and invoice as it arrives, and file it against the right shipment automatically.',
  },
  {
    slug: 'tender-failures',
    title: 'Tender Failures',
    description:
      'When a tender falls through, we re-tender down your routing guide and re-book, before the miss ever reaches your team.',
  },
  {
    slug: 'proactive-load-audits',
    title: 'Proactive Load Audits',
    description:
      'A load that goes untracked is also a load that can’t be audited before pickup.',
  },
  {
    slug: 'scheduling-and-re-scheduling',
    title: 'Scheduling & Re-Scheduling',
    description: 'Runs the same notification loop on the dock side.',
  },
  {
    slug: 'claims-processing',
    title: 'Claims Processing',
    description:
      'We gather the documentation, validate the claim, and draft the response for your approval, so claims actually get filed and recovered.',
  },
];

function TrackingAndStatusUpdates() {
  return (
    <PageWrapper>
      <TrackingAndStatusUpdatesGlobalStyle />
      <Navigation theme="dark" />
      <Hero {...hero} />
      <WatchItRunYourFreight {...watchItRun} />
      <HowItWorks {...howItWorks} />
      <RunsOnYourStack />
      <TheRestOfTheLineup cards={lineupCards} />
      <EndCTA agentName="Tracking & Status Updates" />
      <Footer />
    </PageWrapper>
  );
}

export default TrackingAndStatusUpdates;
export { TrackingAndStatusUpdates };
